import { useEffect, useRef } from 'react';

interface QrCodeCanvasProps {
    /** 二维码内容 URL */
    value: string;
    /** 画布尺寸(px) */
    size?: number;
}

/**
 * 基于 Canvas 的二维码渲染组件
 *
 * 使用简化的 QR 编码算法（仅支持 byte 模式 / L 纠错级），
 * 适用于中短长度的 URL 场景（如微信支付链接）。
 */
export function QrCodeCanvas({ value, size = 192 }: QrCodeCanvasProps) {
    const canvasRef = useRef<HTMLCanvasElement>(null);

    useEffect(() => {
        const canvas = canvasRef.current;
        if (!canvas) return;

        const ctx = canvas.getContext('2d');
        if (!ctx) return;

        try {
            const modules = generateQrMatrix(value);
            const moduleCount = modules.length;
            const moduleSize = size / moduleCount;

            canvas.width = size;
            canvas.height = size;

            // 白色背景
            ctx.fillStyle = '#ffffff';
            ctx.fillRect(0, 0, size, size);

            // 绘制模块
            ctx.fillStyle = '#000000';
            for (let row = 0; row < moduleCount; row++) {
                for (let col = 0; col < moduleCount; col++) {
                    if (modules[row][col]) {
                        ctx.fillRect(
                            col * moduleSize,
                            row * moduleSize,
                            moduleSize,
                            moduleSize
                        );
                    }
                }
            }
        } catch {
            // 编码失败：显示纯文本回退
            canvas.width = size;
            canvas.height = size;
            ctx.fillStyle = '#f3f4f6';
            ctx.fillRect(0, 0, size, size);
            ctx.fillStyle = '#6b7280';
            ctx.font = '12px sans-serif';
            ctx.textAlign = 'center';
            ctx.fillText('扫码支付', size / 2, size / 2 - 10);
            ctx.font = '10px sans-serif';
            ctx.fillText(value.substring(0, 28) + '…', size / 2, size / 2 + 10);
        }
    }, [value, size]);

    return (
        <canvas
            ref={canvasRef}
            width={size}
            height={size}
            style={{ width: size, height: size }}
            aria-label={`QR code: ${value}`}
        />
    );
}

// ========== 简化 QR 编码 ==========

/**
 * 使用 byte 模式 / L 纠错生成 QR 码矩阵。
 * 自动选择适合数据长度的版本（1~10）。
 */
function generateQrMatrix(text: string): boolean[][] {
    const data = new TextEncoder().encode(text);
    const version = pickVersion(data.length);
    const moduleCount = version * 4 + 17;

    // 初始化矩阵
    const matrix: (boolean | null)[][] = Array.from({ length: moduleCount }, () =>
        Array(moduleCount).fill(null)
    );
    const reserved: boolean[][] = Array.from({ length: moduleCount }, () =>
        Array(moduleCount).fill(false)
    );

    // 放置功能图案
    placeFinderPatterns(matrix, reserved, moduleCount);
    placeAlignmentPatterns(matrix, reserved, version, moduleCount);
    placeTimingPatterns(matrix, reserved, moduleCount);
    placeDarkModule(matrix, reserved, version);
    reserveFormatArea(reserved, moduleCount);
    if (version >= 7) reserveVersionArea(reserved, moduleCount);

    // 编码数据
    const encoded = encodeData(data, version);
    placeData(matrix, reserved, moduleCount, encoded);

    // 应用掩码 0（棋盘格）
    applyMask(matrix, reserved, moduleCount);

    // 写入格式信息
    writeFormatInfo(matrix, moduleCount);

    // 转为 boolean
    return matrix.map(row => row.map(cell => cell === true));
}

// 版本容量（byte 模式 / L 纠错）
const VERSION_CAPACITY = [0, 17, 32, 53, 78, 106, 134, 154, 192, 230, 271];

function pickVersion(byteLen: number): number {
    for (let v = 1; v <= 10; v++) {
        if (byteLen <= VERSION_CAPACITY[v]) return v;
    }
    throw new Error('Data too long for QR code version 1-10');
}

// EC 码字数（L 纠错）
const EC_CODEWORDS: Record<number, number> = {
    1: 7, 2: 10, 3: 15, 4: 20, 5: 26, 6: 36, 7: 40, 8: 48, 9: 60, 10: 72
};

// 总码字数
const TOTAL_CODEWORDS: Record<number, number> = {
    1: 26, 2: 44, 3: 70, 4: 100, 5: 134, 6: 172, 7: 196, 8: 242, 9: 292, 10: 346
};

function encodeData(data: Uint8Array, version: number): number[] {
    const totalCodewords = TOTAL_CODEWORDS[version];
    const ecCodewords = EC_CODEWORDS[version];
    const dataCodewords = totalCodewords - ecCodewords;

    // 构建位流
    const bits: number[] = [];

    // 模式指示：byte = 0100
    pushBits(bits, 0b0100, 4);

    // 字符数
    const charCountBits = version <= 9 ? 8 : 16;
    pushBits(bits, data.length, charCountBits);

    // 数据
    for (const byte of data) {
        pushBits(bits, byte, 8);
    }

    // 终止符
    const maxBits = dataCodewords * 8;
    const terminatorLen = Math.min(4, maxBits - bits.length);
    pushBits(bits, 0, terminatorLen);

    // 对齐到字节
    while (bits.length % 8 !== 0) bits.push(0);

    // 填充字节
    const padBytes = [0xec, 0x11];
    let padIdx = 0;
    while (bits.length < maxBits) {
        pushBits(bits, padBytes[padIdx % 2], 8);
        padIdx++;
    }

    // 转码字
    const codewords: number[] = [];
    for (let i = 0; i < bits.length; i += 8) {
        let val = 0;
        for (let j = 0; j < 8; j++) val = (val << 1) | (bits[i + j] || 0);
        codewords.push(val);
    }

    // EC 码字
    const ecWords = generateEC(codewords, ecCodewords);

    // 合并
    const allCodewords = [...codewords, ...ecWords];

    // 转位流
    const result: number[] = [];
    for (const cw of allCodewords) {
        for (let i = 7; i >= 0; i--) {
            result.push((cw >> i) & 1);
        }
    }

    return result;
}

function pushBits(arr: number[], value: number, count: number) {
    for (let i = count - 1; i >= 0; i--) {
        arr.push((value >> i) & 1);
    }
}

// GF(2^8) Reed-Solomon
function generateEC(data: number[], ecCount: number): number[] {
    const gp = generatorPolynomial(ecCount);
    const msg = [...data, ...Array(ecCount).fill(0)];

    for (let i = 0; i < data.length; i++) {
        const coef = msg[i];
        if (coef === 0) continue;
        for (let j = 0; j < gp.length; j++) {
            msg[i + j] ^= gfMul(gp[j], coef);
        }
    }

    return msg.slice(data.length);
}

const GF_EXP = new Uint8Array(512);
const GF_LOG = new Uint8Array(256);

(function initGF() {
    let v = 1;
    for (let i = 0; i < 255; i++) {
        GF_EXP[i] = v;
        GF_LOG[v] = i;
        v <<= 1;
        if (v >= 256) v ^= 0x11d;
    }
    for (let i = 255; i < 512; i++) GF_EXP[i] = GF_EXP[i - 255];
})();

function gfMul(a: number, b: number): number {
    if (a === 0 || b === 0) return 0;
    return GF_EXP[GF_LOG[a] + GF_LOG[b]];
}

function generatorPolynomial(degree: number): number[] {
    let g = [1];
    for (let i = 0; i < degree; i++) {
        const next = Array(g.length + 1).fill(0);
        const root = GF_EXP[i];
        for (let j = 0; j < g.length; j++) {
            next[j] ^= g[j];
            next[j + 1] ^= gfMul(g[j], root);
        }
        g = next;
    }
    return g;
}

// ========== QR 矩阵操作 ==========

function placeFinderPatterns(m: (boolean | null)[][], r: boolean[][], n: number) {
    const positions = [[0, 0], [n - 7, 0], [0, n - 7]];
    for (const [row, col] of positions) {
        for (let dr = -1; dr <= 7; dr++) {
            for (let dc = -1; dc <= 7; dc++) {
                const rr = row + dr, cc = col + dc;
                if (rr < 0 || rr >= n || cc < 0 || cc >= n) continue;
                const inOuter = dr >= 0 && dr <= 6 && dc >= 0 && dc <= 6;
                const inInner = dr >= 2 && dr <= 4 && dc >= 2 && dc <= 4;
                const onBorder = dr === 0 || dr === 6 || dc === 0 || dc === 6;
                m[rr][cc] = inOuter ? (onBorder || inInner) : false;
                r[rr][cc] = true;
            }
        }
    }
}

const ALIGNMENT_POSITIONS: Record<number, number[]> = {
    2: [6, 18], 3: [6, 22], 4: [6, 26], 5: [6, 30], 6: [6, 34],
    7: [6, 22, 38], 8: [6, 24, 42], 9: [6, 26, 46], 10: [6, 28, 50]
};

function placeAlignmentPatterns(m: (boolean | null)[][], r: boolean[][], version: number, n: number) {
    const positions = ALIGNMENT_POSITIONS[version];
    if (!positions) return;

    for (const row of positions) {
        for (const col of positions) {
            // 跳过与定位图案重叠的位置
            if (row <= 8 && col <= 8) continue;
            if (row <= 8 && col >= n - 8) continue;
            if (row >= n - 8 && col <= 8) continue;

            for (let dr = -2; dr <= 2; dr++) {
                for (let dc = -2; dc <= 2; dc++) {
                    const rr = row + dr, cc = col + dc;
                    const val = Math.abs(dr) === 2 || Math.abs(dc) === 2 || (dr === 0 && dc === 0);
                    m[rr][cc] = val;
                    r[rr][cc] = true;
                }
            }
        }
    }
}

function placeTimingPatterns(m: (boolean | null)[][], r: boolean[][], n: number) {
    for (let i = 8; i < n - 8; i++) {
        if (!r[6][i]) { m[6][i] = i % 2 === 0; r[6][i] = true; }
        if (!r[i][6]) { m[i][6] = i % 2 === 0; r[i][6] = true; }
    }
}

function placeDarkModule(m: (boolean | null)[][], r: boolean[][], version: number) {
    const row = 4 * version + 9;
    m[row][8] = true;
    r[row][8] = true;
}

function reserveFormatArea(r: boolean[][], n: number) {
    for (let i = 0; i < 9; i++) {
        if (i < n) r[8][i] = true;
        if (i < n) r[i][8] = true;
    }
    for (let i = 0; i < 8; i++) {
        r[8][n - 1 - i] = true;
        r[n - 1 - i][8] = true;
    }
}

function reserveVersionArea(r: boolean[][], n: number) {
    for (let i = 0; i < 6; i++) {
        for (let j = n - 11; j < n - 8; j++) {
            r[i][j] = true;
            r[j][i] = true;
        }
    }
}

function placeData(m: (boolean | null)[][], r: boolean[][], n: number, bits: number[]) {
    let bitIdx = 0;
    let upward = true;
    for (let col = n - 1; col >= 1; col -= 2) {
        if (col === 6) col = 5; // 跳过 timing 列
        const rows = upward ? range(n - 1, -1) : range(0, n);
        for (const row of rows) {
            for (const dc of [0, -1]) {
                const c = col + dc;
                if (c < 0 || c >= n) continue;
                if (r[row][c]) continue;
                m[row][c] = bitIdx < bits.length ? bits[bitIdx] === 1 : false;
                bitIdx++;
            }
        }
        upward = !upward;
    }
}

function range(start: number, end: number): number[] {
    const arr: number[] = [];
    if (start <= end) {
        for (let i = start; i < end; i++) arr.push(i);
    } else {
        for (let i = start; i > end; i--) arr.push(i);
    }
    return arr;
}

function applyMask(m: (boolean | null)[][], r: boolean[][], n: number) {
    // 掩码 0: (row + col) % 2 == 0
    for (let row = 0; row < n; row++) {
        for (let col = 0; col < n; col++) {
            if (r[row][col]) continue;
            if ((row + col) % 2 === 0) {
                m[row][col] = !m[row][col];
            }
        }
    }
}

function writeFormatInfo(m: (boolean | null)[][], n: number) {
    // L 纠错 + 掩码 0 = 格式字符串 111011111000100
    const formatBits = [1,1,1,0,1,1,1,1,1,0,0,0,1,0,0];

    // 水平
    const hPositions = [0,1,2,3,4,5,7,8,n-8,n-7,n-6,n-5,n-4,n-3,n-2];
    for (let i = 0; i < 15; i++) {
        m[8][hPositions[i]] = formatBits[i] === 1;
    }

    // 垂直
    const vPositions = [n-1,n-2,n-3,n-4,n-5,n-6,n-7,8,7,5,4,3,2,1,0];
    for (let i = 0; i < 15; i++) {
        m[vPositions[i]][8] = formatBits[i] === 1;
    }
}
